import { lineString, point, nearestPointOnLine } from '@turf/turf';
import { hasRepeatedPoints } from './withoutRepeatedPoints';
import { reportDegenerateHoverInput } from './reportDegenerateHoverInput';

/**
 * The wide, invisible hover layers drawn over route lines. Hit-testing against these rather
 * than the visible line gives the same forgiving target the hover highlight uses.
 */
export const ROUTE_HOVER_LAYER_IDS = [
  'consolidated-active-routes-hover',
  'consolidated-routes-hover'
];

interface RenderedFeatureLike {
  layer?: { id?: string };
  properties?: Record<string, any> | null;
}

interface RouteLike {
  id: string;
  lineStrings: Array<{ lineStringGeometry?: { coordinates?: number[][] } } | null | undefined>;
}

export interface RouteLineHit {
  routeId: string;
  routeKey: string;
  lineIndex: number;
  /** The spot on the line nearest the cursor, as [lng, lat]. */
  point: [number, number];
  /** Where a control point placed at `point` belongs in route.markers. */
  insertIndex: number;
  /** Which segment of lineStrings[lineIndex] `point` falls on. */
  segmentIndex: number;
}

export function findRouteHoverFeature<T extends RenderedFeatureLike>(features: T[] | null | undefined): T | null {
  if (!features) return null;
  return features.find(f => !!f.layer?.id && ROUTE_HOVER_LAYER_IDS.includes(f.layer.id)) ?? null;
}

/**
 * Resolves a hover-layer feature to the exact point on the route line under the cursor, and
 * where a control point there would be inserted. LineString[i] connects Marker[i] to
 * Marker[i+1], so a point on line i belongs right after Marker[i].
 */
export function routeLineHitFromFeature(
  feature: RenderedFeatureLike | null | undefined,
  routes: Record<string, RouteLike>,
  lngLat: { lng: number; lat: number }
): RouteLineHit | null {
  const props = feature?.properties;
  if (!props) return null;

  const routeKey = props.routeKey;
  const lineIndex = Number(props.lineIndex);
  if (!routeKey || !(routeKey in routes) || !Number.isInteger(lineIndex)) return null;

  const route = routes[routeKey];
  const coords = route.lineStrings[lineIndex]?.lineStringGeometry?.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;

  // Both inputs have to be sound before turf sees them: a non-finite cursor and a zero-length
  // segment (the same lng/lat twice in a row) each make nearestPointOnLine throw "coordinates
  // must contain numbers".
  //
  // Unlike the hover handler, this one cannot hand turf a de-duplicated copy of the line.
  // `segmentIndex` below is turf's index into the array it was given, and insertMarkerAtPoint
  // uses it to index THIS array when it splits the line and slices the surface ranges — an
  // index shifted by a dropped point would mis-split the route. "No hit" is the safe answer,
  // and mapClicked already handles it: it logs "could not be resolved" and leaves the route
  // alone. The next render sanitizes the line, so the click works again straight after.
  if (!Number.isFinite(lngLat.lng) || !Number.isFinite(lngLat.lat)) {
    reportDegenerateHoverInput('non-finite-cursor', {
      source: 'routeLineHit', routeKey, lineIndex, lng: lngLat.lng, lat: lngLat.lat
    });
    return null;
  }
  if (hasRepeatedPoints(coords)) {
    reportDegenerateHoverInput('repeated-points', {
      source: 'routeLineHit', routeKey, lineIndex, pointCount: coords.length
    });
    return null;
  }

  const nearest = nearestPointOnLine(lineString(coords), point([lngLat.lng, lngLat.lat]));
  const [lng, lat] = nearest.geometry.coordinates;

  return {
    routeId: props.routeId ?? route.id,
    routeKey,
    lineIndex,
    point: [lng, lat],
    insertIndex: lineIndex + 1,
    segmentIndex: nearest.properties.index as number
  };
}

/** The route line under the cursor, if any — optionally only when it belongs to `onlyRouteKey`. */
export function findRouteLineHit(
  features: RenderedFeatureLike[] | null | undefined,
  routes: Record<string, RouteLike>,
  lngLat: { lng: number; lat: number },
  onlyRouteKey?: string | null
): RouteLineHit | null {
  const hit = routeLineHitFromFeature(findRouteHoverFeature(features), routes, lngLat);
  if (!hit) return null;
  if (onlyRouteKey !== undefined && hit.routeKey !== onlyRouteKey) return null;
  return hit;
}
